import React, { useEffect, useState } from "react";
import { featuredProducts } from "../utils/static";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faCheck } from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";
import { services } from "../utils/static";
import API_CLIENT from "../lib/api-client";
import { GET_ALL_CATEGORIES } from "../utils/constant";
import HeroImage from "../assets/image/Hero_image.jpg";
import FeaturesProduct from "../component/FeaturesProduct";
import { CategoryCardSkeleton } from "../component/Skeleton";

const Home = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchCategories = async () => {
      setLoading(true);
      try {
        const response = await API_CLIENT.get(GET_ALL_CATEGORIES);
        if (response.status === 201) {
          setCategories(response.data.categories);
          setLoading(false);
        }
      } catch (error) {
        setCategories([]);
      }
    };
    fetchCategories();
  }, []);

  return (
    <div className="bg-neutral-100 font-sans text-neutral-800">
      {/* Hero Section */}
      <section className="relative min-h-screen bg-white overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={HeroImage}
            alt="Background Image"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-neutral-900/50 backdrop-blur-sm"></div>
        </div>

        <div className="container mx-auto px-6 relative z-10 flex flex-col justify-center items-center h-screen text-white text-center">
          <p className="text-teal-400 font-medium tracking-widest uppercase mb-6 text-sm">
            Welcome to Our Textile World
          </p>
          <h1 className="text-4xl md:text-7xl font-light leading-tight mb-8">
            Explore the Finest Textiles from Around the Globe
          </h1>
          <p className="text-xl md:text-2xl text-neutral-200 max-w-3xl mx-auto leading-relaxed mb-12">
            Discover premium fabrics that combine traditional craftsmanship with
            innovative design
          </p>

          <div className="flex flex-wrap gap-6 mt-8">
            <a
              href="/about"
              className="px-8 py-3 bg-teal-600 text-white rounded-sm transition hover:bg-teal-700"
            >
              About Us
            </a>
            <a
              href="/contact"
              className="px-8 py-3 border border-white/30 hover:border-white/70 text-white rounded-sm transition"
            >
              Inquiry
            </a>
          </div>
        </div>
      </section>

      {/* About/Services Introduction Section - Completely Redesigned */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-20">
              {/* Left Side - Content */}
              <div className="order-2 lg:order-1">
                <p className="text-teal-600 font-medium tracking-widest uppercase mb-4 text-sm">
                  Our Expertise
                </p>
                <h2 className="text-3xl md:text-5xl font-light mb-8 text-neutral-900">
                  Find Perfect Fabric <br />
                  With Our Expertise
                </h2>
                <div className="w-16 h-1 bg-teal-500 mb-8"></div>

                <p className="text-neutral-600 text-lg mb-10 leading-relaxed">
                  It is also associated with the development of garments. The
                  rough material of materials is the fiber which may be
                  customary recovered.
                </p>

                {/* Stat Blocks */}
                <div className="grid grid-cols-2 gap-6 mb-10">
                  <div className="border-l-4 border-teal-500 pl-6">
                    <div className="text-4xl font-light text-neutral-900 mb-2">
                      12+
                    </div>
                    <p className="text-neutral-600">Years of Experience</p>
                  </div>
                  <div className="border-l-4 border-teal-300 pl-6">
                    <div className="text-4xl font-light text-neutral-900 mb-2">
                      200+
                    </div>
                    <p className="text-neutral-600">Premium Fabrics</p>
                  </div>
                </div>

                {/* Feature Cards - Horizontal */}
                <div className="space-y-5">
                  {[
                    {
                      title: "Premium Materials",
                      desc: "From brilliant textures to wearable gadgets.",
                    },
                    {
                      title: "Research & Innovation",
                      desc: "Investigating the job of materials in human culture.",
                    },
                    {
                      title: "Sustainability",
                      desc: "Reasonable material creation rehearses.",
                    },
                  ].map((feature, index) => (
                    <div key={index} className="flex items-center gap-4 group">
                      <div className="w-14 h-14 flex-shrink-0 flex items-center justify-center rounded-sm bg-teal-50 text-teal-600 group-hover:bg-teal-600 group-hover:text-white transition-colors duration-300">
                        <FontAwesomeIcon icon={faCheck} className="text-lg" />
                      </div>
                      <div>
                        <h3 className="text-xl font-light mb-1 text-neutral-900 group-hover:text-teal-600 transition-colors duration-300">
                          {feature.title}
                        </h3>
                        <p className="text-neutral-600">{feature.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* CTA Button */}
                <div className="mt-12">
                  <a
                    href="/about"
                    className="inline-flex items-center gap-3 px-8 py-3 bg-teal-600 text-white rounded-sm transition hover:bg-teal-700"
                  >
                    Learn More About Us
                    <FontAwesomeIcon icon={faArrowRight} />
                  </a>
                </div>
              </div>

              {/* Right Side - Textile Gallery */}
              <div className="order-1 lg:order-2">
                <div className="relative h-full flex items-center">
                  {/* Main Image */}
                  <div className="relative z-10 w-[70%] ml-auto">
                    <div className="relative overflow-hidden">
                      <img
                        src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSKlPnwhSx0EEqWn8AKmNmPwBHpWJ6T6s9BAw&s"
                        alt="Textile machinery with thread spools"
                        className="w-full h-auto object-cover shadow-xl transition-transform duration-700 hover:scale-105"
                      />

                      {/* Decorative pattern overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-teal-900/20 to-transparent mix-blend-multiply"></div>
                    </div>
                  </div>

                  {/* Secondary Image - Positioned to overlap */}
                  <div className="absolute z-20 bottom-16 -left-6 w-[45%] shadow-xl border-8 border-white">
                    <div className="overflow-hidden">
                      <img
                        src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQg8ABX2mOEQmI_38P8uSWQCnR4fDXDE3lhUw&s"
                        alt="Textile detail"
                        className="w-full h-auto transition-transform duration-700 hover:scale-105"
                      />
                    </div>
                  </div>

                  {/* Decorative Elements */}
                  <div className="absolute top-12 left-0 w-24 h-24 border-t-4 border-l-4 border-teal-500 -z-10"></div>
                  <div className="absolute bottom-0 right-12 w-32 h-32 border-r-4 border-b-4 border-teal-300 -z-10"></div>

                  {/* Experience Badge */}
                  <div className="absolute top-10 right-8 bg-white shadow-lg py-3 px-6 z-30">
                    <p className="text-xs uppercase tracking-wide text-teal-700 font-medium">
                      Experience Since
                    </p>
                    <p className="text-3xl font-light text-neutral-900">2010</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Popular Services Section */}
      <section className="py-24 bg-teal-700 text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto mb-16 text-center">
            <p className="text-teal-200 font-medium tracking-widest uppercase mb-4 text-sm">
              Our Services
            </p>
            <h2 className="text-3xl md:text-5xl font-light mb-6">
              Our Popular Services
            </h2>
            <div className="w-16 h-1 bg-white mx-auto mb-8"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
            {services.map((service, index) => (
              <div
                key={index}
                className="group bg-white/10 backdrop-blur-sm p-8 border border-teal-600/30 hover:border-white/30 transition-all duration-300 hover:shadow-lg"
              >
                <div className="flex flex-col items-center justify-center mb-6">
                  <img
                    src={service.image}
                    alt={service.name}
                    className="w-20 h-20 mb-6 transition-transform duration-300 group-hover:scale-110"
                  />
                  <h3 className="text-xl font-light mb-4 text-center">
                    {service.name}
                  </h3>
                </div>
                <p className="text-teal-100 text-center">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Categories Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto mb-16 text-center">
            <p className="text-teal-600 font-medium tracking-widest uppercase mb-4 text-sm">
              Explore
            </p>
            <h2 className="text-3xl md:text-5xl font-light mb-6 text-neutral-900">
              Product Categories
            </h2>
            <div className="w-16 h-1 bg-teal-500 mx-auto mb-8"></div>
          </div>
          {loading ? (
            <section className="py-24 bg-white">
              <div className="container mx-auto px-6">
                {/* Category cards skeleton grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto">
                  {[...Array(3)].map((_, index) => (
                    <CategoryCardSkeleton key={index} />
                  ))}
                </div>
              </div>
            </section>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto">
              {categories.slice(0, 6).map((category) => (
                <Link
                  key={category._id}
                  to={`/categoryDetails/${category._id}`}
                  className="group relative overflow-hidden block"
                >
                  <div className="absolute inset-0 border border-neutral-200 translate-x-2 translate-y-2 z-0 transition-transform duration-300 group-hover:translate-x-4 group-hover:translate-y-4"></div>
                  <div className="relative aspect-[4/3] overflow-hidden z-10">
                    <img
                      src={category.imageUrl}
                      alt={category.name}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-neutral-900/30 group-hover:bg-teal-900/40 transition-all duration-300 flex items-center justify-center">
                      <h3 className="text-white text-2xl font-light px-6 py-3 border border-white/30 group-hover:border-white/70 transition-all duration-300 backdrop-blur-sm">
                        {category.name}
                      </h3>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
          {/* View all category button - Unchanged as requested */}
          <div className="text-center mt-8">
            <Link
              to="/category"
              className="inline-block px-8 py-3 border border-neutral-300 hover:border-teal-500 text-neutral-700 hover:text-teal-600 rounded-sm transition-all duration-300"
            >
              View All category
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Products Section */}
      <section className="py-24 bg-neutral-100">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto mb-16 text-center">
            <p className="text-teal-600 font-medium tracking-widest uppercase mb-4 text-sm">
              Highlights
            </p>
            <h2 className="text-3xl md:text-5xl font-light mb-6 text-neutral-900">
              Featured Products
            </h2>
            <div className="w-16 h-1 bg-teal-500 mx-auto mb-8"></div>
          </div>

          <div className="overflow-hidden">
            <div className="flex">
              <FeaturesProduct />
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-20 bg-teal-700 text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-5xl font-light mb-8">
              Ready to Transform Your Textile Vision?
            </h2>
            <p className="text-teal-100 text-lg mb-12 max-w-2xl mx-auto">
              From concept to creation, we're here to bring your textile ideas
              to life with quality materials and expert craftsmanship.
            </p>
            <div className="flex flex-wrap justify-center gap-6">
              <a
                href="/products"
                className="inline-block px-10 py-4 bg-white text-teal-700 hover:bg-teal-50 rounded-sm transition-colors duration-300 text-lg"
              >
                Explore Products
              </a>
              <a
                href="/contact"
                className="inline-block px-10 py-4 border border-white/50 hover:border-white text-white hover:bg-white/10 rounded-sm transition-colors duration-300 text-lg"
              >
                Contact Us
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
